// Client-side Dispute Progress Tracker (enhancement).
// Shows every dispute letter as a status pill + 30-day FCRA countdown timer.
import { useCallback, useEffect, useMemo, useState } from "react";
import BrandShell from "../components/BrandShell";
import { apiCall } from "../auth/AuthContext";
import { CheckCircle2, Clock, Mail, AlertTriangle, Loader2, FileText } from "lucide-react";

const DAY = 24 * 60 * 60 * 1000;

const RECIPIENT_LABELS = {
  bureau: "Consumer Records Agency",
  furnisher: "Reporting Institution",
  cfpb_escalation: "Regulatory Review",
};

export default function ClientDisputeTracker() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const d = await apiCall("/api/disputes/letters");
      return d.records || [];
    } catch { return []; }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const rows = await load();
      if (cancelled) return;
      setRecords(rows);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [load]);

  return (
    <BrandShell dashboardChrome>
      <div className="bg-jc-black min-h-[calc(100vh-5rem)]">
        <div className="px-6 md:px-12 py-12 max-w-5xl mx-auto" data-testid="client-dispute-tracker">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-jc-gold mb-2">◆ Records Review Tracker</p>
        <h1 className="font-display text-jc-white" style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)", lineHeight: 0.95 }}>
          Every letter. Every status. In real time.
        </h1>
        <p className="mt-4 text-jc-muted max-w-2xl">
          Each records-review letter prepared with your authorization is tracked here. We track the standard 30-day review window for you.
        </p>

        {loading ? (
          <div className="py-20 flex items-center justify-center text-jc-muted gap-3"><Loader2 className="animate-spin" size={18}/> Loading…</div>
        ) : records.length === 0 ? (
          <div className="mt-10 py-16 text-center border border-dashed border-jc-gold/25 rounded-xl" data-testid="dispute-tracker-empty">
            <FileText size={42} className="mx-auto text-jc-gold/50 mb-4"/>
            <p className="text-jc-muted">No review letters yet. Upload a credit report to get an educational read of your profile and map your next steps.</p>
          </div>
        ) : (
          <ol className="mt-10 space-y-4" data-testid="dispute-tracker-list">
            {records.map((r, i) => <LetterCard key={r.id} letter={r} index={i + 1} />)}
          </ol>
        )}
        </div>
      </div>
    </BrandShell>
  );
}

function LetterCard({ letter, index }) {
  const sentAt = letter.sent_at ? new Date(letter.sent_at) : null;
  const now = new Date();
  const daysSinceSent = sentAt ? Math.floor((now - sentAt) / DAY) : null;
  const daysLeft = sentAt ? Math.max(0, 30 - daysSinceSent) : null;
  const overdue = daysLeft === 0 && letter.status === "sent";

  return (
    <li className="border border-jc-gold/20 bg-jc-navy/40 p-5 rounded-xl" data-testid={`tracker-letter-${letter.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold">◆ Letter #{index} · Round {letter.round_number}</p>
          <h3 className="mt-1 font-display text-xl text-jc-white">{letter.recipient_name}</h3>
          <p className="text-xs font-mono uppercase tracking-[0.18em] text-jc-muted mt-1">{RECIPIENT_LABELS[letter.recipient_type] || "Records Review"}</p>
        </div>
        <StatusPill status={letter.status} />
      </div>

      {/* Timeline */}
      <div className="mt-4 flex items-center justify-between gap-2 text-[11px] font-mono uppercase tracking-[0.16em]">
        <Step label="Drafted" done={!!letter.created_at} />
        <Connector />
        <Step label="Approved" done={!!letter.approved_at} />
        <Connector />
        <Step label="Mailed" done={letter.status === "sent"} />
        <Connector />
        <Step label="Response Window" done={false} active={letter.status === "sent"} overdue={overdue}/>
      </div>

      {/* Detail */}
      <div className="mt-5 grid sm:grid-cols-3 gap-4 text-xs">
        <Detail label="Drafted" value={letter.created_at ? new Date(letter.created_at).toLocaleDateString() : "—"} />
        <Detail label="Mailed" value={sentAt ? sentAt.toLocaleDateString() : "—"} />
        <Detail label="Tracking" value={letter.lob_tracking_number || "—"} accent={!!letter.lob_tracking_number} />
      </div>

      {letter.status === "sent" && sentAt && (
        <div className={`mt-5 flex items-center gap-3 px-4 py-3 border rounded ${overdue ? "border-red-500/40 bg-red-500/5" : "border-jc-gold/30 bg-jc-gold/5"}`} data-testid={`tracker-countdown-${letter.id}`}>
          {overdue ? <AlertTriangle size={18} className="text-red-300"/> : <Clock size={18} className="text-jc-gold"/>}
          <div className="flex-1">
            <p className={`font-mono text-xs uppercase tracking-[0.22em] ${overdue ? "text-red-300" : "text-jc-gold"}`}>
              {overdue ? "30-day review window has passed — your specialist is on it" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} remaining in the standard review window`}
            </p>
            <p className="text-xs text-jc-muted mt-0.5">{overdue
              ? "Your specialist is preparing the next step in your records review."
              : `Day ${daysSinceSent + 1} of 30 since mailed.`}
            </p>
          </div>
        </div>
      )}
    </li>
  );
}

function StatusPill({ status }) {
  const map = {
    draft: "bg-amber-500/15 text-amber-200 border-amber-500/30",
    approved: "bg-sky-500/15 text-sky-200 border-sky-500/30",
    sent: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
    error: "bg-red-500/15 text-red-200 border-red-500/30",
  };
  return <span className={`px-3 py-1 text-[10px] font-mono uppercase tracking-[0.22em] border ${map[status] || "bg-jc-muted/15 text-jc-muted border-jc-muted/30"}`} data-testid="tracker-status">{status}</span>;
}

function Step({ label, done, active, overdue }) {
  const color = overdue ? "text-red-300" : done ? "text-jc-gold" : active ? "text-jc-white" : "text-jc-muted/50";
  return (
    <div className={`flex flex-col items-center text-center ${color}`}>
      <span className={`h-2.5 w-2.5 rounded-full ${overdue ? "bg-red-400" : done ? "bg-jc-gold" : active ? "bg-jc-white animate-pulse" : "bg-jc-muted/40"}`}/>
      <span className="mt-1.5 whitespace-nowrap">{label}</span>
    </div>
  );
}
function Connector() { return <div className="flex-1 h-px bg-jc-gold/20"/>; }
function Detail({ label, value, accent }) {
  return (
    <div className="border border-jc-gold/15 px-3 py-2">
      <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-jc-muted">{label}</p>
      <p className={`mt-0.5 ${accent ? "text-jc-gold" : "text-jc-white"} text-sm`}>{value}</p>
    </div>
  );
}
